import { useChat, type Message } from 'ai/react';
import { supabase } from '@/integrations/supabase/client';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { CHAT_FUNCTION } from '@/lib/chatFunction';
import { creerFetchChat } from '@/lib/chatTransport';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
// L'aiguillage abonnement / API se decide par COMPTE, a l'execution
// (src/lib/chatTransport.ts) — plus au moment du build. Les actions JSON
// (projets, versions, getChat) restent de toute facon sur l'edge function.

export type { Message };

interface PixelChatOptions {
  chatId?: string | null;
  projectId?: string | null;
  supabaseConnected?: boolean;
  supabaseProjectName?: string | null;
  supabaseProjectRef?: string | null; // The Supabase project_ref (e.g., 'azlzarszcflmpxvayhlc')
  supabaseAnonKey?: string | null; // The Supabase anon key for the connected project
  // Visual Edit context - passed when element is selected
  visualEditContext?: {
    elementTag?: string;
    elementClass?: string;
    elementXPath?: string;
    elementContent?: string;
  } | null;
  // Project files from WebContainer - for agent context
  projectFiles?: {
    path: string;
    type: 'file' | 'directory';
    content?: string; // Content for small files only
  }[] | null;
  // Getter function for dynamic file context (called on each request)
  getProjectFiles?: () => { path: string; type: 'file' | 'directory'; content?: string }[];
  // Getter for terminal output (on-demand for agent tools)
  getTerminalOutput?: () => string;
  // Callback when usage limit is reached
  onLimitReached?: (info: { plan_type: string; messages_remaining_today: number; messages_remaining_month: number }) => void;
}

export function usePixelChat({ chatId, projectId, supabaseConnected, supabaseProjectName, supabaseProjectRef, supabaseAnonKey, visualEditContext, projectFiles, getProjectFiles, getTerminalOutput, onLimitReached }: PixelChatOptions = {}) {
  const [token, setToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.access_token) {
        setToken(session.access_token);
      }
      // L'adresse du compte decide de la route : abonnement du studio pour
      // Mendar et Dylan, API pour tous les autres.
      setEmail(session?.user?.email ?? null);
      setIsReady(true);
    });
  }, []);

  // Recree uniquement quand le compte change, pas a chaque rendu.
  const fetchChat = useMemo(() => creerFetchChat(email), [email]);

  const chatResult = useChat({
    // Toujours l'edge function comme cible nominale : `fetchChat` devie vers
    // pixel-api pour les seuls comptes concernes, et retombe ici si ce
    // service ne repond pas.
    api: `${SUPABASE_URL}/functions/v1/${CHAT_FUNCTION}`,
    fetch: fetchChat,
    body: {
      chatId,
      projectId,
      supabaseConnected: supabaseConnected || false,
      supabaseProjectName: supabaseProjectName || null,
      supabaseProjectRef: supabaseProjectRef || null,
      supabaseAnonKey: supabaseAnonKey || null,
      visualEditContext: visualEditContext || null,
      // Project files for agent context - use getter for dynamic content, fallback to static
      projectFiles: (() => {
        const files = getProjectFiles?.() || projectFiles || [];
        return files.slice(0, 50).map(f => ({
          path: f.path,
          type: f.type,
          content: f.content?.slice(0, 1500), // Limit content per file
        }));
      })(),
      // Terminal output for agent diagnostics (last 50 lines)
      terminalOutput: getTerminalOutput?.()?.split('\n').slice(-50).join('\n') || '',
    },
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    onError: (error) => {
      console.error("Chat error:", error);

      // Try to parse 429 limit exceeded error
      try {
        const errorData = JSON.parse(error.message);
        if (errorData.error === 'usage_limit_exceeded' && onLimitReached) {
          onLimitReached({
            plan_type: errorData.plan_type,
            messages_remaining_today: errorData.messages_remaining_today,
            messages_remaining_month: errorData.messages_remaining_month,
          });
          return;
        }
      } catch {
        // Not a JSON error, continue with normal toast
      }

      toast.error("Erreur lors de la génération: " + error.message);
    },
  });

  // Safe wrapper pour append qui vérifie que la fonction existe
  const safeAppend = async (message: Message | { role: 'user' | 'assistant'; content: string }) => {
    if (!isReady) {
      toast.error("Chargement en cours, veuillez patienter...");
      return null;
    }

    // Retry logic - append may not be immediately available
    for (let attempt = 0; attempt < 3; attempt++) {
      if (typeof chatResult.append === 'function') {
        return chatResult.append(message as Message);
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 100 * (attempt + 1)));
    }

    console.error("[usePixelChat] append is not available after retries. chatResult:", Object.keys(chatResult));
    toast.error("Le chat n'est pas encore prêt, veuillez réessayer dans quelques secondes.");
    return null;
  };

  // Load existing chat messages from database
  const loadChat = useCallback(async (chatIdToLoad: string) => {
    if (!token) {
      console.warn("[usePixelChat] Cannot load chat: no token");
      return null;
    }

    try {
      console.log("[usePixelChat] Loading chat:", chatIdToLoad);

      const response = await fetch(`${SUPABASE_URL}/functions/v1/${CHAT_FUNCTION}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'getChat', chatId: chatIdToLoad }),
      });

      if (!response.ok) {
        console.error("[usePixelChat] Failed to load chat:", response.status);
        return null;
      }

      const data = await response.json();

      if (data?.messages && data.messages.length > 0) {
        console.log("[usePixelChat] Loaded", data.messages.length, "messages");

        // Transform DB messages to AI SDK format
        // SAFETY: Ensure content is always a string to prevent .trim() crash
        const formattedMessages: Message[] = data.messages.map((m: any) => ({
          id: m.id,
          role: m.role as 'user' | 'assistant',
          content: m.content ?? '',  // Fallback to empty string if null/undefined
        }));

        chatResult.setMessages(formattedMessages);
        return data.chat;
      }

      return data.chat;
    } catch (err) {
      console.error("[usePixelChat] Error loading chat:", err);
      return null;
    }
  }, [token, chatResult.setMessages]);

  // CRITICAL: Sanitize all messages to ensure content is always a string
  // This prevents .trim() crashes from undefined content in tool-only messages
  const sanitizedMessages = useMemo(() => {
    return chatResult.messages.map((m: any) => ({
      ...m,
      content: m.content ?? '',  // Guarantee string content
    }));
  }, [chatResult.messages]);

  return {
    messages: sanitizedMessages,  // Use sanitized messages instead of raw
    input: chatResult.input,
    setInput: chatResult.setInput,
    handleInputChange: chatResult.handleInputChange,
    handleSubmit: chatResult.handleSubmit,
    isLoading: chatResult.isLoading || !isReady,
    stop: chatResult.stop,
    append: safeAppend,
    setMessages: chatResult.setMessages,
    isReady,
    loadChat,
  };
}
